import { useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, FileIcon, PackageIcon, SpinnerIcon } from '../components/icons';
import UploadField from '../components/UploadField';
import PublishSteps from '../components/PublishSteps';
import { useI18n } from '../i18n';
import type { TranslationKey } from '../i18n/translations';

type Stage = 'idle' | 'creating' | 'uploading' | 'building';
const STAGE_KEY: Record<Stage, TranslationKey> = { idle: 'workspace.publishApp', creating: 'create.stage.creating', uploading: 'create.stage.uploading', building: 'create.stage.building' };

export default function CreateApp() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>('idle');
  // Reuse the draft on retry, including when uploading or triggering the build fails.
  const draftId = useRef<string | null>(null);
  const draftName = useRef('');
  const submit = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error(t('workspace.chooseFile'));
      const appName = name.trim();
      setStage('creating');
      if (!draftId.current) {
        const app = await api.createApp(appName);
        draftId.current = app.id;
        draftName.current = appName;
      } else if (appName !== draftName.current) {
        await api.renameApp(draftId.current, appName);
        draftName.current = appName;
      }
      setStage('uploading');
      await api.uploadZip(draftId.current, file);
      setStage('building');
      await api.triggerBuild(draftId.current);
      return draftId.current;
    },
    onSuccess: (appId) => { queryClient.invalidateQueries({ queryKey: ['apps'] }); navigate('/apps/' + appId); },
    onSettled: () => { setStage('idle'); queryClient.invalidateQueries({ queryKey: ['apps'] }); },
  });
  const busy = submit.isPending;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (name.trim() && file && !busy) submit.mutate();
  }

  return (
    <div className="workspace-page">
      <Link to="/dashboard" className="back-link"><ArrowLeftIcon size={16} />{t('common.backToApps')}</Link>
      <header className="workspace-header"><div><p className="eyebrow">{t('workspace.publishEyebrow')}</p><h1>{t('workspace.publishTitle')}</h1><p className="page-subtitle">{t('workspace.publishSub')}</p></div></header>
      <PublishSteps active={busy && stage === 'building' ? 1 : 0} />
      <div className="publish-layout">
        <form className="surface publish-form" onSubmit={handleSubmit}>
          <div className="section-heading"><h2>{t('workspace.projectDetails')}</h2><span className="subtle-label">{t('workspace.requiredFields')}</span></div>
          <div className="form-group"><label className="form-label" htmlFor="app-name">{t('create.nameLabel')}</label><input id="app-name" className="form-input" placeholder={t('workspace.nameExample')} value={name} maxLength={40} required disabled={busy} autoComplete="off" onChange={(event) => setName(event.target.value)} aria-describedby="app-name-hint" /><p className="form-hint" id="app-name-hint">{t('create.nameHint')}</p></div>
          <div className="form-group"><label className="form-label" htmlFor="project-file">{t('create.zipLabel')}</label><UploadField file={file} disabled={busy} onChange={setFile} /></div>
          {busy && <div className="inline-notice" role="status"><SpinnerIcon size={18} />{t(STAGE_KEY[stage])}</div>}
          {submit.error && <div className="state-box state-error" role="alert"><div><strong>{t('workspace.publishError')}</strong><p>{(submit.error as Error).message}</p><p>{t('workspace.retryHint')}</p></div></div>}
          <div className="publish-form-footer"><p><CheckIcon size={15} />{t('workspace.buildInCloud')}</p><div className="form-actions"><button type="button" className="btn btn-ghost" disabled={busy} onClick={() => navigate('/dashboard')}>{t('common.cancel')}</button><button type="submit" className="btn btn-primary" disabled={!name.trim() || !file || busy}>{busy ? <SpinnerIcon size={16} /> : <ArrowRightIcon size={16} />}{busy ? t(STAGE_KEY[stage]) : t(submit.isError ? 'workspace.retryPublish' : 'workspace.publishApp')}</button></div></div>
        </form>
        <aside className="publish-sidebar">
          <section className="surface guidance-card"><span className="guidance-icon"><FileIcon size={22} /></span><h2>{t('workspace.zipHelpTitle')}</h2><p>{t('workspace.zipHelpSub')}</p><ol className="guidance-list"><li>{t('workspace.zipHelp1')}</li><li>{t('workspace.zipHelp2')}</li><li>{t('workspace.zipHelp3')}</li></ol><div className="supported-projects"><h3>{t('workspace.supported')}</h3><span>HTML / CSS / JavaScript</span><span>React / Vue / Vite</span><span>Android / Kotlin / Java</span></div></section>
          <section className="next-card"><PackageIcon size={21} /><h3>{t('workspace.whatNext')}</h3><p>{t('workspace.whatNextSub')}</p></section>
        </aside>
      </div>
    </div>
  );
}
